import { useTranslation } from 'react-i18next';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import { CalendarMode, CALENDAR_MODES } from '@/helpers/calendarMode';
import styles from './DensityToggle.module.scss';

interface CalendarModeToggleProps {
  value: CalendarMode;
  onChange: (mode: CalendarMode) => void;
  showOnMobile?: boolean;
  /**
   * Compact control for datepicker chrome (HAT-1249). Always visible —
   * the board toggle is covered by the mobile portal / sits outside the
   * open picker, so this copy has to stay reachable without closing it.
   */
  embedded?: boolean;
}

const LABEL_KEYS: Record<CalendarMode, string> = {
  gregorian: 'calendarGregorian',
  hebrew: 'calendarHebrew',
};

const CalendarModeToggle = ({ value, onChange, showOnMobile = false, embedded = false }: CalendarModeToggleProps) => {
  const { t } = useTranslation();
  const { isSidebarDocked } = useDeviceDetection();

  if (!embedded && !isSidebarDocked && !showOnMobile) return null;

  return (
    <div className={`${styles.container} ${showOnMobile ? styles.mobileEnabled : ''} ${embedded ? styles.embedded : ''}`}>
      <div className={styles.group} role="radiogroup" aria-label={t('calendarMode')} data-testid="calendar-mode-toggle" data-embedded={embedded || undefined}>
        {CALENDAR_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={value === mode}
            aria-pressed={value === mode}
            tabIndex={value === mode ? 0 : -1}
            data-calendar-option={mode}
            className={`${styles.option} ${value === mode ? styles.optionSelected : ''} body-sm-regular`}
            onClick={() => onChange(mode)}
            onKeyDown={(event) => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const currentIndex = Math.max(0, CALENDAR_MODES.indexOf(mode));
              const rtl = typeof document !== 'undefined' && document.dir === 'rtl';
              const step = event.key === 'ArrowLeft' ? (rtl ? 1 : -1) : event.key === 'ArrowRight' ? (rtl ? -1 : 1) : 0;
              const nextIndex =
                event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? CALENDAR_MODES.length - 1
                    : (currentIndex + step + CALENDAR_MODES.length) % CALENDAR_MODES.length;
              const next = CALENDAR_MODES[nextIndex];
              onChange(next);
              event.currentTarget.parentElement?.querySelector<HTMLButtonElement>(`[data-calendar-option="${next}"]`)?.focus();
            }}
          >
            {t(LABEL_KEYS[mode])}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CalendarModeToggle;
